/**
 * Thin wrapper around DocuSign's Digital Referral library (`DSDigitalSignup`).
 *
 * The library is a small script hosted by DocuSign that drives two flows from a
 * partner app:
 *   - `startSignup` opens a popup for the free DocuSign trial signup + account
 *     provisioning workflow, optionally pre-filling the form with user details.
 *   - `viewPlans` opens DocuSign's plans & pricing page so a (trial) user can
 *     purchase a paid plan.
 *
 * The script exposes itself as `window.DSDigitalSignup`. We load it lazily and
 * fire the calls synchronously inside the click handler so the popup is treated
 * as a user-initiated action (browsers block popups opened after an `await`).
 * Call `preloadDocusignDigitalSignup()` on mount so the script is ready by the
 * time the user clicks.
 *
 * Ref: https://developers.docusign.com/partner/digital-referral/
 */

const SCRIPT_URL = 'https://www.docusign.com/scripts/ds-digital-signup.js'

// Our DocuSign integration key doubles as the partner integration key (partnerIK)
// used for referral attribution.
const PARTNER_IK = import.meta.env.VITE_DOCUSIGN_INTEGRATION_ID

// Australian locale — DocuSign uses this to localise the signup/plans pages.
const LOCALE = 'en-AU'

interface DSDigitalSignupApi {
    /** (firstName, lastName, workEmail, phone, partnerIK, loginRedirectUri, locale) */
    startSignup: (
        firstName?: string,
        lastName?: string,
        workEmail?: string,
        phone?: string,
        partnerIK?: string,
        loginRedirectUri?: string,
        locale?: string,
    ) => void
    /** (locale) */
    viewPlans: (locale?: string) => void
}

declare global {
    interface Window {
        DSDigitalSignup?: DSDigitalSignupApi
    }
}

let loader: Promise<DSDigitalSignupApi> | undefined

const loadDSDigitalSignup = (): Promise<DSDigitalSignupApi> => {
    if (window.DSDigitalSignup) return Promise.resolve(window.DSDigitalSignup)
    if (loader) return loader

    loader = new Promise<DSDigitalSignupApi>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = SCRIPT_URL
        script.async = true
        script.onload = () => {
            if (window.DSDigitalSignup) resolve(window.DSDigitalSignup)
            else reject(new Error('DSDigitalSignup unavailable after script load'))
        }
        script.onerror = () => {
            loader = undefined // allow a later retry
            reject(new Error('Failed to load the DocuSign Digital Referral script'))
        }
        document.head.appendChild(script)
    })

    return loader
}

/**
 * Kick off loading the Digital Referral script ahead of time so the trial/plans
 * buttons can fire synchronously (and therefore avoid the popup blocker).
 */
export const preloadDocusignDigitalSignup = () => {
    void loadDSDigitalSignup().catch(() => {})
}

export interface DocusignTrialPrefill {
    firstName?: string
    lastName?: string
    email?: string
    phone?: string
}

/**
 * Launch the DocuSign free-trial signup workflow in a popup. Optional prefill
 * values pre-populate the signup form; `loginRedirectUri` is set to the current
 * page so the user returns to e-Signing settings after activating their account.
 */
export const startDocusignTrial = (prefill: DocusignTrialPrefill = {}) => {
    const fire = (ds: DSDigitalSignupApi) =>
        ds.startSignup(
            prefill.firstName,
            prefill.lastName,
            prefill.email,
            prefill.phone,
            PARTNER_IK,
            window.location.href,
            LOCALE,
        )

    // Fire synchronously when the script is already loaded so the popup keeps the
    // user gesture; otherwise load first (the popup may be blocked in this case).
    if (window.DSDigitalSignup) fire(window.DSDigitalSignup)
    else void loadDSDigitalSignup().then(fire).catch(err => console.error(err))
}

/** Open DocuSign's plans & pricing page (for purchasing after the trial). */
export const viewDocusignPlans = () => {
    if (window.DSDigitalSignup) window.DSDigitalSignup.viewPlans(LOCALE)
    else void loadDSDigitalSignup().then(ds => ds.viewPlans(LOCALE)).catch(err => console.error(err))
}
